import Breadcrumb from "components/Common/Breadcrumb";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Form,
  Input,
  Label,
  Modal,
  Row,
} from "reactstrap";
import Select from "react-select";
import { Editor } from "@tinymce/tinymce-react";
import { fetchNodeData, FetchNodePutData } from "utils/common-fetchData";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";
import Loader from "pages/Separate/Loader";
import { copyToClipBoard } from "pages/Separate/copyText";
import configure from "configure";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";

const LettersEditTemplate = () => {
  // --------------------------------------------------------------------- //
  //  UI / STATE
  // --------------------------------------------------------------------- //
  const [fixButtons, setFixButtons] = useState(false);
  const [edit_status, setEditStatus] = useState(false);
  const [loading, setLoading] = useState(false);

  const [options, setOptions] = useState([]); // Letter-type <Select> options
  const [selectedOption, setSelectedOption] = useState(null);

  const [templateData, setTemplateData] = useState({
    name: "",
    description: "", // will hold the **HTML string** from TinyMCE
  });

  const navigate = useNavigate();
  const { id } = useParams();

  // --------------------------------------------------------------------- //
  //  FETCH LETTER TYPES (same as Add)
  // --------------------------------------------------------------------- //
  const fetchLetterTypes = async () => {
    const response = await fetchNodeData(
      setLoading,
      `LetterTypes`,
      secureLocalStorage.getItem("auth_token")
    );
    if (response.status) {
      const formatted = response.data.map((x) => ({
        value: x.id,
        label: x.LetterName,
      }));
      setOptions(formatted);
    } else {
      setOptions([]);
    }
  };

  // --------------------------------------------------------------------- //
  //  FETCH TEMPLATE (prefill)
  // --------------------------------------------------------------------- //
  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await fetchNodeData(
        setLoading,
        `staffTemplate/${id}`,
        secureLocalStorage.getItem("auth_token")
      );

      if (response.status === "success") {
        const { name, description, type } = response.data;
        setTemplateData({
          name: name || "",
          description: description || "<p></p>",
        });

        // pre-select the correct Letter Type
        if (type && options.length) {
          const match = options.find((o) => o.value === Number(type));
          setSelectedOption(match || null);
        }
      } else {
        Swal.fire({
          icon: "error",
          text: response.data?.message || "Failed to load template",
          showConfirmButton: true,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: err.message || "Error loading template",
        showConfirmButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------- //
  //  AFTER OPTIONS ARE LOADED → set selectedOption if we already have type
  // --------------------------------------------------------------------- //
  useEffect(() => {
    if (templateData.type !== undefined && options.length) {
      const match = options.find((o) => o.value === Number(templateData.type));
      setSelectedOption(match || null);
    }
  }, [options, templateData.type]);

  // --------------------------------------------------------------------- //
  //  SAVE (PUT) – same payload as Add
  // --------------------------------------------------------------------- //
  const handleSave = async () => {
    if (!templateData.name || !selectedOption?.value) {
      Swal.fire({ icon: "warning", text: "Name and Document Type are required" });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        id, // required for PUT
        name: templateData.name,
        type: selectedOption.value,
        description: templateData.description, // HTML string
      };

      const response = await FetchNodePutData(
        setLoading,
        `staffTemplate`,
        id,
        payload
      );

      if (response.status === "success" || response.status === 200) {
        Swal.fire({ icon: "success", text: "Template updated successfully" });
        navigate(`${configure.appUrl}setting/companysetting?tab=4`);
      } else {
        Swal.fire({
          icon: "error",
          text: response.data?.message || "Failed to update template",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: err.message || "An error occurred",
      });
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------- //
  //  TINY MCE → store the **HTML** string
  // --------------------------------------------------------------------- //
  const handleEditorChange = (content, _editor) => {
    setTemplateData((prev) => ({ ...prev, description: content }));
  };

  // --------------------------------------------------------------------- //
  //  FIXED BUTTONS (scroll)
  // --------------------------------------------------------------------- //
  useEffect(() => {
    const handler = () => setFixButtons(window.scrollY > 160);
    document.addEventListener("scroll", handler);
    return () => document.removeEventListener("scroll", handler);
  }, []);

  // --------------------------------------------------------------------- //
  //  INITIAL LOAD
  // --------------------------------------------------------------------- //
  useEffect(() => {
    fetchLetterTypes();
  }, []);

  useEffect(() => {
    if (id) fetchTemplate();
  }, [id, options]); // re-run when options become available

  // --------------------------------------------------------------------- //
  //  RENDER
  // --------------------------------------------------------------------- //
  return (
    <div>
      {loading && <Loader />}

      <React.Fragment>
        <div className="page-content mb-4">
          <Container fluid>
            <Breadcrumb
              title="Dashboard"
              breadcrumbItem="Edit Document Template"
            />

            <Row className="gx-4 justify-content-between di-flex align-items-top">
              {/* FIXED BUTTONS */}
              <div
                className={`flex-column fixed-buttons ${
                  fixButtons ? "d-flex" : "d-none"
                }`}
              >
                <button
                  className="btn btn-purple border-radius fix-btn"
                  onClick={handleSave}
                >
                  <i className="fas fa-save me-2"></i>
                  <span>Save</span>
                </button>
                <a
                  onClick={() => navigate(-1)}
                  className="btn btn-red border-radius fix-btn"
                >
                  <i className="dripicons-cross me-2 icon-center"></i>
                  <span>Cancel</span>
                </a>
              </div>

              <Card>
                <CardBody>
                  <div className="mb-20">
                    <Row>
                      <div className="col-md-6">
                        <h4>Edit Document Template</h4>
                      </div>
                      <div className="col-md-6 text-md-end">
                        <button
                          className="border-radius btn text-white me-md-0 me-2 btn-purple"
                          onClick={handleSave}
                        >
                          <i className="fas fa-save me-2"></i>SAVE
                        </button>
                        <a
                          className="border-radius btn text-white ms-md-2 btn-red"
                          onClick={() => navigate(-1)}
                        >
                          <i className="dripicons-cross me-2 icon-center"></i>
                          CANCEL
                        </a>
                      </div>
                    </Row>
                  </div>

                  <Form className="row align-items-end">
                    {/* NAME */}
                    <div className="mb-3 col-md-3">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        type="text"
                        required
                        className="form-control"
                        id="name"
                        placeholder="Enter Name"
                        value={templateData.name}
                        onChange={(e) =>
                          setTemplateData({
                            ...templateData,
                            name: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* LETTER TYPE */}
                    <div className="mb-3 col-md-3">
                      <Label>Document Type</Label>
                      <Select
                        required
                        className="basic-multi-select"
                        classNamePrefix="select"
                        options={options}
                        value={selectedOption}
                        onChange={(val) => setSelectedOption(val)}
                      />
                    </div>

                    {/* DYNAMIC FIELDS BUTTON */}
                    <div className="mb-3 col-md-3">
                      <a
                        className="border-radius btn text-white me-md-0 me-2 btn-purple"
                        onClick={() => setEditStatus(true)}
                      >
                        Dynamic Fields
                      </a>
                    </div>

                    {/* DESCRIPTION (TinyMCE) */}
                    <div className="mb-3 col-md-12">
                      <Label>Description</Label>
                      <Editor
                        apiKey="w6fz9u4rgsouz7jqcyy4lflpolp0tjwkx1bzoc70ntetxwys"
                        value={templateData.description}
                        init={{
                          height: 800,
                          menubar: true,
                          plugins: [
                            "a11ychecker",
                            "accordion",
                            "advlist",
                            "advcode",
                            "advtable",
                            "autolink",
                            "checklist",
                            "export",
                            "lists",
                            "link",
                            "image",
                            "charmap",
                            "anchor",
                            "searchreplace",
                            "visualblocks",
                            "powerpaste",
                            "fullscreen",
                            "formatpainter",
                            "insertdatetime",
                            "media",
                            "table",
                            "wordcount",
                            "code",
                            "pagebreak",
                            "quickbars",
                            "codesample",
                            "textcolor",
                          ],
                          toolbar:
                            "undo redo | casechange blocks | bold italic backcolor | fontselect fontsizeselect |" +
                            "alignleft aligncenter alignright alignjustify | " +
                            "bullist numlist checklist outdent indent | removeformat | a11ycheck code table | exportpdf |",
                          fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
                        }}
                        onEditorChange={handleEditorChange}
                      />
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Row>
          </Container>
        </div>

        {/* -------------------------- DYNAMIC FIELDS MODAL -------------------------- */}
        {/* <Modal isOpen={edit_status} size="xl" centered>
          <div className="modal-header">
            <h5 className="modal-title mt-0">Dynamic fields</h5>
            <button
              type="button"
              onClick={() => setEditStatus(false)}
              className="btn-modal-close"
            >
              <span aria-hidden="true" className="btn-modal-span">
                ×
              </span>
            </button>
          </div>

          <div className="modal-body">
            <Row>
                        <div className="d-flex flex-wrap position-relative">
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2 ">
                            <label htmlFor="" className="col-4 mb-0">
                              Employee Name
                            </label>
                            <div className="p-2 me-3 col-5 bg-dynamic-field">
                              {"$emp_name"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(0)}
                              value="$emp_name"
                              className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary  btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Joining Date
                            </label>
                            <div className="p-2 me-3 col-5 bg-dynamic-field">
                              {"$join_date"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(1)}
                              value="$join_date"
                              className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Employee Id
                            </label>
                            <div className="p-2 me-3 col-5 bg-dynamic-field">
                              {"$emp_id"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(2)}
                              value="$emp_id"
                              className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Designation
                            </label>
                            <div className="p-2 me-3 col-5 bg-dynamic-field">
                              {"$designation"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(3)}
                              value="$designation"
                              className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Basic Salary
                            </label>
                            <div className="p-2 me-3 col-5 bg-dynamic-field">
                              {"$basic_salary"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(4)}
                              value="$basic_salary"
                              className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Dearness Allowance
                            </label>
                            <div className="p-2 me-3 col-5 bg-dynamic-field">
                              {"$dearness_allowance"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(5)}
                              value="$dearness_allowance"
                              className="p-2 fas fa-copy copyBtn  h-100 col-2 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              House Rent Allowance
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">
                              {"$house_rent"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(6)}
                              value="$house_rent"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Conveyance
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">
                              {"$conveyance"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(7)}
                              value="$conveyance"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Address line 1
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">
                              {"$address_1"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(8)}
                              value="$address_1"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Address line 2
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">
                              {"$address_2"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(9)}
                              value="$address_2"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              City
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">{"$city"}</div>
                            <button
                              onClick={() => copyToClipBoard(10)}
                              value="$city"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              State
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">
                              {"$state"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(11)}
                              value="$state"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Country
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">
                              {"$country"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(12)}
                              value="$country"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                            <label htmlFor="" className="col-4 mb-0">
                              Pincode
                            </label>
                            <div className="p-2 col-5 me-3 bg-dynamic-field">
                              {"$pincode"}
                            </div>
                            <button
                              onClick={() => copyToClipBoard(13)}
                              value="$pincode"
                              className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                            ></button>
                          </div>
                          <div className="copy-modal-line"></div>
                        </div>
                      </Row>
          </div>
        </Modal> */}
        {edit_status ? (
          <Draggable
            handle=".drag-header" // drag only by header
            bounds="body" // prevent moving off-screen
          >
            <div
              style={{
                position: "fixed",
                top: "10%", // <<< SET YOUR OWN POSITION HERE
                left: "15%",
                transform: "translateX(-50%)",
                zIndex: 999999,
              }}
            >
              {/* RESIZABLE WRAPPER */}
              <ResizableBox
                width={1000}
                height={540}
                minConstraints={[650, 350]}
                maxConstraints={[1500, 900]}
                resizeHandles={["se"]}
              >
                {/* MAIN MODAL CONTENT */}
                <div
                  className="modal-content"
                  style={{
                    height: "100%",
                    // overflow: "auto",
                    zIndex: 999999,
                  }}
                >
                  {/* HEADER (DRAG HANDLE) */}
                  <div
                    className="modal-header drag-header"
                    // style={{
                    //   cursor: "move",
                    //   userSelect: "none",
                    // }}
                  >
                    <h5 className="modal-title mt-0 mb-3">Dynamic Fields</h5>

                    <button
                      type="button"
                      onClick={() => setEditStatus(false)}
                      className="btn-modal-close"
                    >
                      <span aria-hidden="true" className="btn-modal-span">
                        ×
                      </span>
                    </button>
                  </div>

                  {/* BODY */}
                  <div className="modal-body">
                    <Row>
                      <div className="d-flex flex-wrap position-relative">
                        {/* ---------------------------- */}
                        {/* FULL DYNAMIC FIELDS LIST     */}
                        {/* ---------------------------- */}

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2 ">
                          <label htmlFor="" className="col-4 mb-0">
                            Employee Name
                          </label>
                          <div className="p-2 me-3 col-5 bg-dynamic-field">
                            {"$emp_name"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(0)}
                            value="$emp_name"
                            className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Joining Date
                          </label>
                          <div className="p-2 me-3 col-5 bg-dynamic-field">
                            {"$join_date"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(1)}
                            value="$join_date"
                            className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Employee Id
                          </label>
                          <div className="p-2 me-3 col-5 bg-dynamic-field">
                            {"$emp_id"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(2)}
                            value="$emp_id"
                            className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Designation
                          </label>
                          <div className="p-2 me-3 col-5 bg-dynamic-field">
                            {"$designation"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(3)}
                            value="$designation"
                            className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Basic Salary
                          </label>
                          <div className="p-2 me-3 col-5 bg-dynamic-field">
                            {"$basic_salary"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(4)}
                            value="$basic_salary"
                            className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Dearness Allowance
                          </label>
                          <div className="p-2 me-3 col-5 bg-dynamic-field">
                            {"$dearness_allowance"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(5)}
                            value="$dearness_allowance"
                            className="p-2 fas fa-copy copyBtn h-100 col-2 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            House Rent Allowance
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$house_rent"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(6)}
                            value="$house_rent"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Conveyance
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$conveyance"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(7)}
                            value="$conveyance"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Address line 1
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$address_1"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(8)}
                            value="$address_1"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Address line 2
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$address_2"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(9)}
                            value="$address_2"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            City
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$city"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(10)}
                            value="$city"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            State
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$state"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(11)}
                            value="$state"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Country
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$country"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(12)}
                            value="$country"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Pincode
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$pincode"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(13)}
                            value="$pincode"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>
                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Company name
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$company_name"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(13)}
                            value="$company_name"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>
                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Shift start and end time
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$shift_start_and_end_time"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(13)}
                            value="$shift_start_and_end_time"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>
                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Total salary
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$total_salary"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(13)}
                            value="$total_salary"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>
                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Department
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$department"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(13)}
                            value="$department"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>
                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Position
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$position"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(13)}
                            value="$position"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>
                        <div className="col-6 px-3 align-items-center d-flex justify-content-center px-1 mb-2">
                          <label htmlFor="" className="col-4 mb-0">
                            Date of resignation
                          </label>
                          <div className="p-2 col-5 me-3 bg-dynamic-field">
                            {"$date_of_resignation"}
                          </div>
                          <button
                            onClick={() => copyToClipBoard(13)}
                            value="$date_of_resignation"
                            className="p-2 col-2 fas fa-copy copyBtn h-100 btn btn-primary btn-sm"
                          ></button>
                        </div>

                        <div className="copy-modal-line"></div>
                      </div>
                    </Row>
                  </div>
                </div>
              </ResizableBox>
            </div>
          </Draggable>
        ) : null}
      </React.Fragment>
    </div>
  );
};

export default LettersEditTemplate;
